import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Cloud,
  IndianRupee,
  Percent,
  Search,
  Wallet,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BillingFiltersBar } from '../../../components/MasterAdmin/BillingAnalytics/BillingFiltersBar';
import {
  DATA_START_DATE,
  GrowthBadge,
  ShareBar,
  SortHeader,
  chartTooltipStyle,
  formatCompact,
  formatCurrency,
  formatDateShort,
  paletteColor,
  tdClass,
  thClass,
} from '../../../components/MasterAdmin/BillingAnalytics/billingUi';
import InsightsPageShell from '../../../components/MasterAdmin/insights/InsightsPageShell';
import type { MetricInfoDefinition } from '../../../components/MasterAdmin/ui/MetricInfoPopover';
import OperationsStatCard from '../../../components/MasterAdmin/operations/OperationsStatCard';
import { OpsChartTitle } from '../../../components/MasterAdmin/operations/OpsMetricLabels';
import { opsChartCard, opsFilterClass, opsMetricGridClass } from '../../../components/MasterAdmin/operations/operationsUi';
import { getBillingServices, getServiceHistory, getTimeRangeDates } from '../../../services/MasterAdmin/billingAnalyticsService';

const PAGE_INFO: MetricInfoDefinition = {
  definition: 'Net cost, credits, usage, and growth for every GCP service in the selected period. Expand a row to see its daily spend.',
  sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['service.id', 'cost', 'credits', 'usage'] }],
};

type ServiceRow = {
  id: string;
  name: string;
  netCost: number;
  grossCost: number;
  credits: number;
  currency: string;
  usage: string;
  resources: string | number;
  growth: string;
  share: number;
  color: string;
};

const BillingServicesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const timeRange = searchParams.get('timeRange') || 'This Month';
  const projectId = searchParams.get('projectId') || 'vedika-health';
  const customStart = searchParams.get('startDate') || undefined;
  const customEnd = searchParams.get('endDate') || undefined;
  const serviceId = searchParams.get('serviceId') || undefined;
  const resourceName = searchParams.get('resourceName') || undefined;

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [servicesData, setServicesData] = useState<ServiceRow[]>([]);
  const [hasValidPreviousData, setHasValidPreviousData] = useState(false);
  const [historyData, setHistoryData] = useState<Record<string, Array<{ date: string; rawDate: string; netCost: number }>>>({});
  const [loadingHistory, setLoadingHistory] = useState<Record<string, boolean>>({});
  const [sortField, setSortField] = useState('netCost');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const fetchServices = async () => {
      if (!projectId) return;
      try {
        setIsLoading(true);
        setError(null);
        const { start, end, prevStart } = getTimeRangeDates(timeRange, customStart, customEnd);
        const validPrev = new Date(prevStart) >= DATA_START_DATE;
        setHasValidPreviousData(validPrev);

        const data = await getBillingServices(start, end, projectId, serviceId, resourceName);
        const totalNet = (data || []).reduce((sum: number, s: any) => sum + Number(s.netCost || 0), 0);
        setServicesData(
          (data || []).map((s: any, idx: number) => ({
            id: s.serviceId,
            name: s.serviceDescription || s.serviceId || 'Unknown service',
            netCost: Number(s.netCost || 0),
            grossCost: Number(s.grossCost || 0),
            credits: Math.abs(Number(s.credits || 0)),
            currency: s.currency || 'INR',
            usage: s.usage || 'N/A',
            resources: s.resources ?? 'N/A',
            growth: validPrev ? s.growth || 'N/A' : 'N/A',
            share: totalNet > 0 ? (Number(s.netCost || 0) / totalNet) * 100 : 0,
            color: paletteColor(idx),
          }))
        );
      } catch (err) {
        console.error('Failed to fetch services data:', err);
        setError('Unable to load service billing. Please retry.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchServices();
  }, [timeRange, projectId, customStart, customEnd, serviceId, resourceName]);

  const toggleExpand = async (id: string) => {
    const isExpanding = expandedId !== id;
    setExpandedId(isExpanding ? id : null);
    if (!isExpanding || historyData[id]) return;
    try {
      setLoadingHistory((prev) => ({ ...prev, [id]: true }));
      const { start, end } = getTimeRangeDates(timeRange, customStart, customEnd);
      const data = await getServiceHistory(id, start, end, projectId);
      setHistoryData((prev) => ({
        ...prev,
        [id]: (data || []).map((t: any) => ({
          date: t.date ? new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '',
          rawDate: t.date,
          netCost: Number(t.netCost || 0),
        })),
      }));
    } catch (err) {
      console.error('Failed to fetch history for service:', id, err);
    } finally {
      setLoadingHistory((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleSort = (field: string) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q ? servicesData.filter((s) => s.name.toLowerCase().includes(q) || s.id?.toLowerCase().includes(q)) : servicesData;
    return [...rows].sort((a, b) => {
      let aVal: any = a[sortField as keyof ServiceRow];
      let bVal: any = b[sortField as keyof ServiceRow];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [servicesData, query, sortField, sortOrder]);

  const totals = useMemo(() => {
    const net = servicesData.reduce((s, r) => s + r.netCost, 0);
    const gross = servicesData.reduce((s, r) => s + r.grossCost, 0);
    const credits = servicesData.reduce((s, r) => s + r.credits, 0);
    const top = [...servicesData].sort((a, b) => b.netCost - a.netCost)[0];
    return { net, gross, credits, top, count: servicesData.length, currency: servicesData[0]?.currency || 'INR' };
  }, [servicesData]);

  const pieData = useMemo(() => {
    const billed = [...servicesData].filter((s) => s.netCost > 0).sort((a, b) => b.netCost - a.netCost);
    const top = billed.slice(0, 6);
    const rest = billed.slice(6).reduce((sum, s) => sum + s.netCost, 0);
    if (rest > 0) top.push({ ...billed[0], id: 'others', name: 'Others', netCost: rest, share: totals.net > 0 ? (rest / totals.net) * 100 : 0, color: '#94a3b8' });
    return top;
  }, [servicesData, totals.net]);

  const barData = useMemo(
    () =>
      [...servicesData]
        .filter((s) => s.netCost > 0)
        .sort((a, b) => b.netCost - a.netCost)
        .slice(0, 8)
        .map((s) => ({
          name: s.name.length > 22 ? `${s.name.slice(0, 20)}…` : s.name,
          fullName: s.name,
          netCost: s.netCost,
          fill: s.color,
        })),
    [servicesData]
  );

  return (
    <InsightsPageShell
      title="Services Billing"
      description="Cost, credits, usage, and growth for every GCP service in the selected period."
      info={PAGE_INFO}
    >
      <BillingFiltersBar />

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900/40 dark:bg-red-900/20">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="h-72 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            <div className="h-72 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            <OperationsStatCard label="Net cost" infoKey="totalCostNet" value={formatCurrency(totals.net, totals.currency)} sub="All services this period" icon={IndianRupee} />
            <OperationsStatCard label="Gross cost" infoKey="totalGrossCost" value={formatCurrency(totals.gross, totals.currency)} sub="Before credits" icon={Wallet} />
            <OperationsStatCard
              label="Credits saved"
              infoKey="totalCredits"
              value={formatCurrency(totals.credits, totals.currency)}
              sub={totals.gross > 0 ? `${((totals.credits / totals.gross) * 100).toFixed(1)}% of list price` : 'Discounts applied'}
              icon={Percent}
              accent="text-emerald-600 dark:text-emerald-400"
            />
            <OperationsStatCard
              label="Services"
              infoKey="totalServicesUsed"
              value={String(totals.count)}
              sub={totals.top ? `${totals.top.name} is ${totals.top.share.toFixed(0)}%` : 'No spend yet'}
              icon={Cloud}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
            <div className={`${opsChartCard} xl:col-span-3`}>
              <OpsChartTitle title="Top services by net cost" infoKey="topServicesChart" />
              {barData.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">No service spend in this period</p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} layout="vertical" margin={{ left: 8, right: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatCompact} />
                      <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip
                        {...chartTooltipStyle}
                        formatter={(value: number) => [formatCurrency(value, totals.currency), 'Net cost']}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                      />
                      <Bar dataKey="netCost" radius={[0, 6, 6, 0]} maxBarSize={22}>
                        {barData.map((entry) => (
                          <Cell key={entry.fullName} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className={`${opsChartCard} xl:col-span-2`}>
              <OpsChartTitle title="Cost mix" infoKey="costDistribution" />
              {pieData.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">No service spend in this period</p>
              ) : (
                <>
                  <div className="relative h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={52} outerRadius={78} paddingAngle={2} dataKey="netCost" nameKey="name" stroke="none">
                          {pieData.map((entry) => (
                            <Cell key={entry.id} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip {...chartTooltipStyle} formatter={(value: number, name: string) => [formatCurrency(value, totals.currency), name]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Net</span>
                      <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{formatCompact(totals.net)}</span>
                    </div>
                  </div>
                  <div className="mt-2 max-h-36 space-y-1.5 overflow-y-auto">
                    {pieData.map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="truncate text-xs font-medium text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="shrink-0 text-xs font-semibold tabular-nums">{item.share.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">All services</p>
                <p className="mt-0.5 text-xs text-gray-500">Click a row to open daily spend for that service.</p>
              </div>
              <label className={`${opsFilterClass} flex max-w-xs items-center gap-2`}>
                <Search className="h-4 w-4 shrink-0 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search services"
                  className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
                />
              </label>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/80">
                  <tr>
                    <th className={`${thClass} w-10`} />
                    <th className={`${thClass} text-left`}>
                      <SortHeader label="Service" field="name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Gross" field="grossCost" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Credits" field="credits" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Net cost" field="netCost" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                    <th className={`${thClass} text-left`}>Usage</th>
                    <th className={`${thClass} text-center`}>
                      <div className="flex justify-center">
                        <SortHeader label="Resources" field="resources" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="center" />
                      </div>
                    </th>
                    <th className={`${thClass} text-left`}>
                      <SortHeader label="Share" field="share" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Growth" field="growth" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                        {query ? 'No services match your search' : 'No services billed in this period'}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((service) => {
                      const isExpanded = expandedId === service.id;
                      const history = historyData[service.id] || [];
                      const peak = history.reduce<(typeof history)[0] | null>((best, row) => (!best || row.netCost > best.netCost ? row : best), null);
                      return (
                        <React.Fragment key={service.id}>
                          <tr
                            className={`cursor-pointer bg-white hover:bg-gray-50 dark:bg-gray-900/40 dark:hover:bg-gray-800/60 ${isExpanded ? 'bg-primary-50/40 dark:bg-primary-950/20' : ''}`}
                            onClick={() => toggleExpand(service.id)}
                          >
                            <td className={tdClass}>
                              {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                            </td>
                            <td className={tdClass}>
                              <div className="flex items-center gap-2">
                                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: service.color }} />
                                <div>
                                  <p className="font-semibold text-gray-900 dark:text-white">{service.name}</p>
                                  <p className="text-[11px] text-gray-400">GCP service</p>
                                </div>
                              </div>
                            </td>
                            <td className={`${tdClass} text-right font-mono text-gray-500`}>{formatCurrency(service.grossCost, service.currency)}</td>
                            <td className={`${tdClass} text-right font-mono text-emerald-600 dark:text-emerald-400`}>
                              {formatCurrency(service.credits, service.currency)}
                            </td>
                            <td className={`${tdClass} text-right font-mono font-semibold text-gray-900 dark:text-white`}>
                              {formatCurrency(service.netCost, service.currency)}
                            </td>
                            <td className={`${tdClass} max-w-[180px] truncate text-xs text-gray-500`} title={service.usage}>
                              {service.usage}
                            </td>
                            <td className={`${tdClass} text-center font-semibold`}>{service.resources}</td>
                            <td className={tdClass}>
                              <ShareBar share={service.share} color={service.color} />
                            </td>
                            <td className={`${tdClass} text-right`}>
                              <GrowthBadge growth={service.growth} valid={hasValidPreviousData} />
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50/80 dark:bg-gray-900/30">
                              <td colSpan={9} className="px-4 py-4">
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                                  <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Service snapshot</p>
                                    <dl className="mt-3 space-y-2 text-sm">
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Net cost</dt>
                                        <dd className="font-semibold">{formatCurrency(service.netCost, service.currency)}</dd>
                                      </div>
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Share of bill</dt>
                                        <dd className="font-semibold">{service.share.toFixed(1)}%</dd>
                                      </div>
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Resources</dt>
                                        <dd className="font-semibold">{service.resources}</dd>
                                      </div>
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Peak day</dt>
                                        <dd className="font-semibold">
                                          {peak ? `${formatDateShort(peak.rawDate)} · ${formatCurrency(peak.netCost, service.currency)}` : '—'}
                                        </dd>
                                      </div>
                                    </dl>
                                  </div>
                                  <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:col-span-2">
                                    <p className="mb-3 text-xs font-semibold text-gray-700 dark:text-gray-200">Daily net cost</p>
                                    {loadingHistory[service.id] ? (
                                      <div className="h-48 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700" />
                                    ) : history.length === 0 ? (
                                      <p className="py-16 text-center text-sm text-gray-500">No daily history for this service</p>
                                    ) : (
                                      <div className="h-48">
                                        <ResponsiveContainer width="100%" height="100%">
                                          <AreaChart data={history}>
                                            <defs>
                                              <linearGradient id={`svc-${service.id}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={service.color} stopOpacity={0.35} />
                                                <stop offset="100%" stopColor={service.color} stopOpacity={0} />
                                              </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompact} width={48} />
                                            <Tooltip
                                              {...chartTooltipStyle}
                                              formatter={(value: number) => [formatCurrency(value, service.currency), 'Net cost']}
                                              labelFormatter={(_, payload) =>
                                                payload?.[0]?.payload?.rawDate ? formatDateShort(payload[0].payload.rawDate) : ''
                                              }
                                            />
                                            <Area type="monotone" dataKey="netCost" stroke={service.color} fill={`url(#svc-${service.id})`} strokeWidth={2} />
                                          </AreaChart>
                                        </ResponsiveContainer>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
                {filtered.length > 0 && (
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold dark:border-gray-700 dark:bg-gray-800/80">
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500">
                        Total
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(totals.gross, totals.currency)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatCurrency(totals.credits, totals.currency)}</td>
                      <td className="px-4 py-3 text-right font-mono text-primary-600">{formatCurrency(totals.net, totals.currency)}</td>
                      <td colSpan={4} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
    </InsightsPageShell>
  );
};

export default BillingServicesPage;
